import type { ComponentType } from 'react';
import type { Task, TaskStatus, ProjectMember, ProjectChat } from '@/types';

export type TaskViewId = 'board' | 'list' | 'workload';
export type ChatViewId = 'internal-chat' | 'client-chat';
export type ViewId = TaskViewId | ChatViewId;

export interface ViewProps {
  projectId: string;
  listId: string;
  tasks: Task[];
  statuses: TaskStatus[];
  members: ProjectMember[];
  isLoading: boolean;
  isClient: boolean;
}

export interface ChatViewProps {
  projectId: string;
  chat: ProjectChat;
}

export interface ViewDefinition {
  id: ViewId;
  label: string;
  icon: string;
  component: ComponentType<ViewProps>;
  isChatView?: boolean;
}

const viewRegistry: ViewDefinition[] = [];

export function registerView(view: ViewDefinition): void {
  const existingIndex = viewRegistry.findIndex((v) => v.id === view.id);
  if (existingIndex >= 0) {
    viewRegistry[existingIndex] = view;
  } else {
    viewRegistry.push(view);
  }
}

export function getView(id: ViewId): ViewDefinition | undefined {
  return viewRegistry.find((v) => v.id === id);
}

export function getAllViews(): ViewDefinition[] {
  return [...viewRegistry];
}

export function getTaskViews(): ViewDefinition[] {
  return viewRegistry.filter((v) => !v.isChatView);
}

export function getDefaultViewId(): TaskViewId {
  return 'board';
}

export function isValidViewId(id: string): id is ViewId {
  return ['board', 'list', 'workload', 'internal-chat', 'client-chat'].includes(id);
}

export function isTaskViewId(id: string): id is TaskViewId {
  return ['board', 'list', 'workload'].includes(id);
}

export function isChatViewId(id: string): id is ChatViewId {
  return ['internal-chat', 'client-chat'].includes(id);
}
